import { mount } from "svelte";
import App from "./App.svelte";
import { transport } from "./transport";
import "./assets/main.css";

/**
 * How long the first render will wait on the host before going ahead without it.
 *
 * Capabilities are resolved before the first render so components never flash the wrong mode — a
 * "Browser mode" banner appearing for a frame inside Revit reads as a bug. But waiting on the host
 * with nothing on screen means every way the host can be slow becomes a blank panel, which is the
 * single least diagnosable thing this app can show and reads as far more broken than a wrong banner
 * for one frame. So the wait is capped: past this, the app boots with whatever it knows, and
 * init() updates the capabilities when it eventually answers.
 */
const HOST_HANDSHAKE_BUDGET_MS = 2000;

const budget = new Promise<void>((resolve) => setTimeout(resolve, HOST_HANDSHAKE_BUDGET_MS));

// init() never rejects — a host that cannot answer degrades to unknown capabilities.
void Promise.race([transport.init(), budget]).finally(() => {
  mount(App, { target: document.getElementById("app")! });
});

// Inside Revit there is no DevTools window to open, so anything that escapes to the top level is
// invisible unless it is written to the console: the host bridges console output into the plugin
// log, and that log is the only view into this page there is. An unhandled rejection in particular
// left no trace at all before this — the symptom was a spinner that never stopped, with nothing
// anywhere to say why.
window.addEventListener("unhandledrejection", (event) => {
  console.error("Unhandled promise rejection:", event.reason);
});

window.addEventListener("error", (event) => {
  console.error("Uncaught error:", event.message, event.filename, event.lineno, event.error);
});
